import { stegaClean } from '@/lib/utils/stega';
import type { BlockImageDto } from '@/lib/sanity/dto';

/**
 * Readable form of the asset's file name — extension dropped, separators turned
 * into spaces ('team-foto_02.jpg' → 'team foto 02'). Last resort for a name:
 * a file name read aloud is poor, but silence is worse.
 */
function resolveFilenameLabel(filename?: string): string {
    return stegaClean(filename || '')
        .replace(/\.[^.]+$/, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Accessible alt text for a projected Sanity image: the asset's `altText`, then
 * the media library `title`, then the caller's fallback (which knows the
 * context — a person's name, the block title), and the asset's file name as the
 * last resort. So the result is effectively never empty — set the alt text in
 * Sanity to keep it from degrading to a file name.
 *
 * Purely decorative images must NOT use this helper: pass `alt=""` directly.
 *
 * `alt` is an attribute, not rendered text, so stega encoding is stripped here
 * (see AGENTS.md → Stega encoding).
 */
export function resolveImageAlt(image?: BlockImageDto | null, fallback = ''): string {
    const authored = stegaClean(image?.alt || image?.title || '');

    return authored || stegaClean(fallback) || resolveFilenameLabel(image?.filename);
}

/**
 * Accessible name for a control whose only content is an image (lightbox or
 * gallery triggers). The caption wins over the asset metadata: it describes the
 * image in context, and when it is also rendered inside the control the name
 * still contains the visible text (WCAG 2.5.3 "Label in Name"). `fallback` is
 * the last resort behind the whole alt cascade, file name included.
 */
export function resolveImageActionLabel(
    image: BlockImageDto | undefined | null,
    action: string,
    caption?: string,
    fallback = 'Bild',
): string {
    const cleanCaption = caption ? stegaClean(caption).trim() : '';
    const subject = cleanCaption || resolveImageAlt(image) || fallback;

    return `${subject} ${action}`;
}
